import React, { useMemo } from 'react';

import { Box, Column, GridContainer, Row } from '@vp/swan';
import { useLocalizations } from '@vp/ubik-localizations-provider';

import { CarouselData } from '../domain/CarouselData';
import { Combination } from '../domain/Combination';
import { SlideArray } from '../domain/SlideArray';

import { Gallery, GoToSlide } from './Gallery';

export interface CarouselProps {
  tenant: string;
  carouselData: CarouselData;
  selectedCombination: Combination;
  mpvId: string;
  productKey: string;
  disableZoom?: boolean;
  productPageUrl?: string;
}

const toGoToSlide = (index: number): GoToSlide => ({ slideNumber: index + 1 });

export const Carousel: React.FC<CarouselProps> = (props) => {
  const { t } = useLocalizations();

  const currentSlideIndex = useMemo<GoToSlide | undefined>(() => {
    const brandkitImage = props.carouselData.getBrandkitImageIndex(props.selectedCombination);
    if (brandkitImage !== null) {
      return toGoToSlide(brandkitImage);
    }

    const documentIndex = props.carouselData.getDocumentImageIndex(props.selectedCombination);
    if (documentIndex !== null) {
      return toGoToSlide(documentIndex);
    }

    if (props.selectedCombination.isInitial) {
      return undefined;
    }

    const referentialImageIndex = props.carouselData.getReferentialImageIndex(
      props.selectedCombination
    );

    return referentialImageIndex !== null ? toGoToSlide(referentialImageIndex) : undefined;
  }, [props.selectedCombination, props.carouselData]);

  const scenesForCombination = useMemo<SlideArray>(
    () => props.carouselData.getSlidesByCombination(props.selectedCombination),
    [props.selectedCombination, props.carouselData]
  );

  return (
    <Box aria-label={t('carousel-container-label')} role="group">
      <GridContainer>
        <Row>
          <Column span={12}>
            <Gallery
              key={scenesForCombination.galleryKey}
              mediaArray={scenesForCombination}
              goToSlide={currentSlideIndex}
              tenant={props.tenant}
              mpvId={props.mpvId}
              productKey={props.productKey}
              disableZoom={props.disableZoom}
              productPageUrl={props.productPageUrl}
            />
          </Column>
        </Row>
      </GridContainer>
    </Box>
  );
};
